import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Send, AlertCircle } from "lucide-react";
import Layout from "@/components/Layout";
import FormLogo from "@/components/FormLogo";
import EmergencyToggle from "@/components/EmergencyToggle";
import { useAuth } from "@/contexts/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Section, Field, RadioGroup, CheckboxGroup, FileField, TopNotice,
  ApplicantSpocSection, RequestedBySection, SelfSkipSection, makeUploader,
  isHodDropdownOptional, stripSelfSkipTransients, ResubmitFromSection
} from "@/components/FormShell";

const TRAVELLER_CATEGORIES = ["Students", "Staff", "Coordinators", "HOD", "Managers",
  "Senior Managers", "HOS", "CAO", "Bursar", "Guest", "Parents"];
const STUDENT_COUNTS = ["Single", "Double", "Triple", "Multiple"];
const VEHICLE_TYPES = ["School Bus", "School Car", "Maini", "Van", "Sedan Car / Taxi",
  "SUV Car / Taxi", "Vendor Bus", "Sharing Vehicle"];
const SHARING_OPTIONS = ["2", "3", "4", "5", "6", "More than 6"];
const PAYERS = ["School", "Student Impress Account", "Staff", "Parents"];
// Phase 23 — "Student Impress" added to the Payment Mode dropdown.
// Keeping the existing options (Phase 19 "Paid by school", Cash, UPI).
const PAYMENT_MODES = ["Online Via UPI Scan", "Cash", "Paid by school", "Student Impress"];

const emptyLeg = () => ({ pickDate: "", pickTime: "", pickLocation: "", pickLink: "",
                          dropDate: "", dropTime: "", dropLocation: "", dropLink: "" });
const emptyStudent = () => ({ name: "", grade: "", type: "Day Scholar" });

export default function NewTransportation() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [depts, setDepts] = useState([]);
  const [hods, setHods] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [isResubmit, setIsResubmit] = useState(false);
  const [parentRefCode, setParentRefCode] = useState(null);

  const [form, setForm] = useState({
    staffName: user?.name || "", staffEmail: user?.email || "", staffContact: "",
    mainDept: "", subDept: "", hodApproval: "", is_hod_self: undefined, is_admin1_self: undefined, admin1_self_cost: "", admin1_self_remark: "",  // Phase 5.2: HOD restored — applicant picks own dept HOD like other forms
    // III
    purposeOfTravel: "", tripType: "Round Trip",
    // One-way (used if tripType = One Way)
    oneWay: emptyLeg(),
    // Round-trip (departure + return)
    departure: emptyLeg(), return: emptyLeg(),
    // IV
    travellerCategories: [], studentsCount: "Single",
    parentContact: "", parentEmail: "",
    students: [emptyStudent()],
    parentsApproved: "No", groupSpocName: "", groupSpocEmail: "", groupSpocContact: "",
    howManyTravelling: "", howManySeaters: "",
    vehicleType: "", sharing: "", bigLuggage: "", smallLuggage: "",
    carrierVehicle: "No", attendantRequired: "No", attendantChargesConsent: "No",
    // V
    medicalConditions: "", allergies: "", emergencyContact: "", schoolEmergencyContact: "",
    // VI
    paymentPaidBy: "", paymentMode: "",
    is_emergency: false, emergency_reason: "",
    // Phase 23 — Consent redesign: 10 statements displayed as a red
    // bulleted list, with ONE required checkbox at the end. Legacy per-
    // key `consents` dict is still accepted by the backend validator so
    // historical records keep rendering.
    applicant_consent_agreed: false,
    // Phase 9 (item 3a/3d/3f)
    trip_purpose_type: "official",
    payment_paid_by: "",
    parent_email: "",
    trip_cancelled: false,
    trip_cancel_reason: "",
  });

  useEffect(() => {
    api.get("/departments").then((r) => setDepts(r.data)).catch(() => {});
    api.get("/hods").then((r) => setHods(r.data)).catch(() => {});
  }, []);

  const subDepts = useMemo(() => {
    const d = depts.find((x) => x.name === form.mainDept);
    return d?.sub_departments || [];
  }, [depts, form.mainDept]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setNested = (parent, k, v) => setForm((f) => ({ ...f, [parent]: { ...f[parent], [k]: v } }));
  const onUpload = makeUploader(api, setAttachments, setUploading, toast, formatApiError);

  const addStudent = () => setForm((f) => ({ ...f, students: [...f.students, emptyStudent()] }));
  const removeStudent = (i) => setForm((f) => ({ ...f, students: f.students.filter((_, idx) => idx !== i) }));
  const setStudent = (i, k, v) => setForm((f) => ({
    ...f, students: f.students.map((s, idx) => idx === i ? { ...s, [k]: v } : s),
  }));

  const hasStudents = form.travellerCategories.includes("Students");

  const validate = () => {
    if (!(form.is_hod_self === true || form.is_admin1_self === true) && !String(form.hodApproval || "").trim()) {
      toast.error("Please select an HOD or declare Yes on Are you a HOD? / Admin-1?");
      return false;
    }
    for (const k of ["staffName", "staffEmail", "staffContact", "mainDept",
                     "subDept",  "purposeOfTravel"]) {
      if (!String(form[k] || "").trim()) {
        toast.error(`Please fill: ${k}`); return false;
      }
    }
    if (form.travellerCategories.length === 0) {
      toast.error("Select at least one traveller category."); return false;
    }
    if (form.is_emergency && (form.emergency_reason || "").trim().length < 10) {
      toast.error("Emergency Reason is mandatory (min 10 characters).");
      return false;
    }
    // Phase 23 — single-checkbox consent gate.
    if (!form.applicant_consent_agreed) {
      toast.error("Please tick 'I agree to all the above-mentioned terms and conditions' before submitting.");
      return false;
    }
    // Phase 9 (item 3d) — if payment_paid_by == "parent", parent_email is required.
    const pby = String(form.payment_paid_by || "").toLowerCase();
    if ((pby === "parent" || pby === "parents") &&
        !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(String(form.parent_email || "").trim())) {
      toast.error("Parent email is required when payment_paid_by == 'parent'.");
      return false;
    }
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // Phase 23 — Promote the optional supporting-document attachment
      // (if any) to a first-class form_data field for tracker + email
      // body consumption. The underlying attachment row stays in the
      // generic `attachments` array for backwards-compat.
      const _sd = (attachments || []).find((a) => a.field_name === "supporting_document");
      const supporting_document = _sd
        ? {
            filename: _sd.filename, url: _sd.url, download_url: _sd.url,
            size_bytes: _sd.size, size: _sd.size,
            uploaded_at: _sd.uploaded_at, field_name: _sd.field_name,
          }
        : null;
      const { data } = await api.post("/requisitions/transportation", {
        form_type: "transportation",
        parent_ref_code: parentRefCode || undefined,
        form_data: {
          ...stripSelfSkipTransients(form),
          attachments,
          supporting_document,
        },
      });
      toast.success(`Submitted — ${data.ref_code}`);
      nav(`/requisitions/${data.ref_code}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const renderLeg = (obj, keyRoot, label) => (
    <div className="grid sm:grid-cols-2 gap-4">
      <div className="sheet-plain p-4">
        <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold mb-3">{label} · Pick-up</div>
        <div className="grid gap-3">
          <Field label="Pick date" required>
            <input type="date" className="input-vgws" value={obj.pickDate}
                   onChange={(e) => setNested(keyRoot, "pickDate", e.target.value)}
                   data-testid={`${keyRoot}-pickDate`} />
          </Field>
          <Field label="Pick time" required>
            <input type="time" className="input-vgws" value={obj.pickTime}
                   onChange={(e) => setNested(keyRoot, "pickTime", e.target.value)}
                   data-testid={`${keyRoot}-pickTime`} />
          </Field>
          <Field label="Pick-up location — describe in detail" required>
            <textarea className="input-vgws min-h-[60px]" value={obj.pickLocation}
                      onChange={(e) => setNested(keyRoot, "pickLocation", e.target.value)}
                      data-testid={`${keyRoot}-pickLocation`} />
          </Field>
          <Field label="Or attach location link">
            <input type="url" className="input-vgws" placeholder="Google Maps link"
                   value={obj.pickLink}
                   onChange={(e) => setNested(keyRoot, "pickLink", e.target.value)}
                   data-testid={`${keyRoot}-pickLink`} />
          </Field>
        </div>
      </div>
      <div className="sheet-plain p-4">
        <div className="text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold mb-3">{label} · Drop</div>
        <div className="grid gap-3">
          <Field label="Drop date" required>
            <input type="date" className="input-vgws" value={obj.dropDate}
                   onChange={(e) => setNested(keyRoot, "dropDate", e.target.value)}
                   data-testid={`${keyRoot}-dropDate`} />
          </Field>
          <Field label="Drop time" required>
            <input type="time" className="input-vgws" value={obj.dropTime}
                   onChange={(e) => setNested(keyRoot, "dropTime", e.target.value)}
                   data-testid={`${keyRoot}-dropTime`} />
          </Field>
          <Field label="Drop location — describe in detail" required>
            <textarea className="input-vgws min-h-[60px]" value={obj.dropLocation}
                      onChange={(e) => setNested(keyRoot, "dropLocation", e.target.value)}
                      data-testid={`${keyRoot}-dropLocation`} />
          </Field>
          <Field label="Or attach location link">
            <input type="url" className="input-vgws" placeholder="Google Maps link"
                   value={obj.dropLink}
                   onChange={(e) => setNested(keyRoot, "dropLink", e.target.value)}
                   data-testid={`${keyRoot}-dropLink`} />
          </Field>
        </div>
      </div>
    </div>
  );

  return (
    <Layout>
      <Toaster position="bottom-right" richColors />
      <div className="max-w-5xl mx-auto relative">
        <FormLogo />
        <div className="mb-4 text-[11px] uppercase tracking-widest text-[#2d6b6b] font-semibold">
          VGWS-TPT-2026 · Transportation Requisition
        </div>
        <h1 className="font-serif text-3xl sm:text-4xl font-bold text-[#1f2320] leading-tight">
          Transportation Requisition Form
        </h1>
        <p className="text-sm text-[#4a524c] mt-1">
          Vishwashanti Gurukul World School · Rajbaug, Loni Kalbhor
        </p>

        <TopNotice>
          <div className="font-serif font-semibold mb-1 flex items-center gap-2">
            <AlertCircle size={16} /> Important
          </div>
          <ul className="list-disc pl-5 space-y-0.5">
            <li>For Pune local travel, submit this form at least <strong>2 days</strong> prior.</li>
            <li>For out-of-Pune / non-local travel, submit at least <strong>4 days</strong> prior.</li>
          </ul>
        </TopNotice>

        <EmergencyToggle
          isEmergency={form.is_emergency}
          onToggle={(v) => setForm((f) => ({ ...f, is_emergency: v, emergency_reason: v ? f.emergency_reason : "" }))}
          emergencyReason={form.emergency_reason}
          onReasonChange={(v) => setForm((f) => ({ ...f, emergency_reason: v }))}
        />

        <form className="mt-6 space-y-6" onSubmit={onSubmit} data-testid="transportation-form">
          <ResubmitFromSection
            isResubmit={isResubmit}
            onToggle={(v) => { setIsResubmit(v); if (!v) setParentRefCode(null); }}
            parentRefCode={parentRefCode}
            onSelectHistory={(fd, ref) => {
              setForm((f) => ({ ...f, ...fd,
                staffName: f.staffName || fd.staffName || "",
                staffEmail: f.staffEmail || fd.staffEmail || "",
              }));
              setParentRefCode(ref);
            }}
          />

          <ApplicantSpocSection form={form} setField={setField} />
          <SelfSkipSection form={form} setField={setField} userRole={user?.role} />
          <RequestedBySection form={form} setField={setField} depts={depts} hods={hods} subDepts={subDepts} hodOptional={isHodDropdownOptional(form)} />

          <Section numeral="III" title="Travel details">
            <Field label="Purpose of travelling" required>
              <textarea className="input-vgws min-h-[70px]" required value={form.purposeOfTravel}
                        onChange={(e) => setField("purposeOfTravel", e.target.value)}
                        data-testid="field-purposeOfTravel" />
            </Field>
            <Field label="Round trip or one way" required>
              <RadioGroup name="tripType" value={form.tripType}
                          onChange={(v) => setField("tripType", v)}
                          options={["Round Trip", "One Way"]} testid="field-tripType" />
            </Field>

            {form.tripType === "One Way" && renderLeg(form.oneWay, "oneWay", "One-way journey")}
            {form.tripType === "Round Trip" && (
              <>
                {renderLeg(form.departure, "departure", "Departure journey")}
                {renderLeg(form.return, "return", "Return journey")}
              </>
            )}

            <FileField label="Attach ticket booking files (airport/railway/bus reference)"
                       fieldName="ticketBooking"
                       accept=".pdf,.jpg,.jpeg,.png"
                       attachments={attachments}
                       onChange={(e) => onUpload("ticketBooking", e)}
                       disabled={uploading} testid="attach-ticketBooking" optional />
          </Section>

          <Section numeral="IV" title="Passengers & vehicle">
            <Field label="Who is travelling" required hint="Select at least one">
              <CheckboxGroup value={form.travellerCategories}
                             onChange={(v) => setField("travellerCategories", v)}
                             options={TRAVELLER_CATEGORIES} testid="field-travellerCategories" />
            </Field>

            {hasStudents && (
              <>
                <Field label="Number of students travelling" required>
                  <RadioGroup name="studentsCount" value={form.studentsCount}
                              onChange={(v) => setField("studentsCount", v)}
                              options={STUDENT_COUNTS} testid="field-studentsCount" />
                </Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Parent's contact number" required>
                    <input className="input-vgws" value={form.parentContact}
                           onChange={(e) => setField("parentContact", e.target.value)}
                           data-testid="field-parentContact" />
                  </Field>
                  <Field label="Parent's email" required>
                    <input type="email" className="input-vgws" value={form.parentEmail}
                           onChange={(e) => setField("parentEmail", e.target.value)}
                           data-testid="field-parentEmail" />
                  </Field>
                </div>
                <div className="space-y-3">
                  {form.students.map((s, i) => (
                    <div key={i} className="sheet-plain p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="font-serif font-semibold text-[#7a1f2b] text-sm">Student {i + 1}</div>
                        {form.students.length > 1 && (
                          <button type="button" onClick={() => removeStudent(i)} className="text-[#b3261e] text-xs" data-testid={`remove-student-${i}`}>Remove</button>
                        )}
                      </div>
                      <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Student name" required>
                          <input className="input-vgws" value={s.name}
                                 onChange={(e) => setStudent(i, "name", e.target.value)}
                                 data-testid={`student-name-${i}`} />
                        </Field>
                        <Field label="Grade" required>
                          <input className="input-vgws" value={s.grade}
                                 onChange={(e) => setStudent(i, "grade", e.target.value)}
                                 data-testid={`student-grade-${i}`} />
                        </Field>
                        <Field label="Day scholar / Residential" required>
                          <RadioGroup name={`sType-${i}`} value={s.type}
                                      onChange={(v) => setStudent(i, "type", v)}
                                      options={["Day Scholar", "Residential"]}
                                      testid={`student-type-${i}`} />
                        </Field>
                      </div>
                    </div>
                  ))}
                  <button type="button" className="btn-ghost" onClick={addStudent} data-testid="add-student">
                    + Add another student
                  </button>
                </div>
                <Field label="Approved by parents?" required hint="Confirm before submission.">
                  <RadioGroup name="parentsApproved" value={form.parentsApproved}
                              onChange={(v) => setField("parentsApproved", v)}
                              options={["Yes", "No"]} testid="field-parentsApproved" />
                </Field>
              </>
            )}

            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Group SPOC name">
                <input className="input-vgws" value={form.groupSpocName}
                       onChange={(e) => setField("groupSpocName", e.target.value)}
                       data-testid="field-groupSpocName" />
              </Field>
              <Field label="Group SPOC email">
                <input type="email" className="input-vgws" value={form.groupSpocEmail}
                       onChange={(e) => setField("groupSpocEmail", e.target.value)}
                       data-testid="field-groupSpocEmail" />
              </Field>
              <Field label="Group SPOC contact number">
                <input className="input-vgws" value={form.groupSpocContact}
                       onChange={(e) => setField("groupSpocContact", e.target.value)}
                       data-testid="field-groupSpocContact" />
              </Field>
              <Field label="How many travelling" required>
                <input type="number" min="1" className="input-vgws" value={form.howManyTravelling}
                       onChange={(e) => setField("howManyTravelling", e.target.value)}
                       data-testid="field-howManyTravelling" />
              </Field>
              <Field label="How many seaters" required>
                <input type="number" min="1" className="input-vgws" value={form.howManySeaters}
                       onChange={(e) => setField("howManySeaters", e.target.value)}
                       data-testid="field-howManySeaters" />
              </Field>
              <Field label="Type of vehicle" required>
                <select className="input-vgws" value={form.vehicleType}
                        onChange={(e) => setField("vehicleType", e.target.value)}
                        data-testid="field-vehicleType">
                  <option value="">Select vehicle</option>
                  {VEHICLE_TYPES.map((v) => <option key={v}>{v}</option>)}
                </select>
              </Field>
              {form.vehicleType === "Sharing Vehicle" && (
                <Field label="Number of sharing" required>
                  <select className="input-vgws" value={form.sharing}
                          onChange={(e) => setField("sharing", e.target.value)}
                          data-testid="field-sharing">
                    <option value="">Select</option>
                    {SHARING_OPTIONS.map((v) => <option key={v}>{v}</option>)}
                  </select>
                </Field>
              )}
              <Field label="Big luggage count">
                <input type="number" min="0" className="input-vgws" value={form.bigLuggage}
                       onChange={(e) => setField("bigLuggage", e.target.value)}
                       data-testid="field-bigLuggage" />
              </Field>
              <Field label="Small luggage count">
                <input type="number" min="0" className="input-vgws" value={form.smallLuggage}
                       onChange={(e) => setField("smallLuggage", e.target.value)}
                       data-testid="field-smallLuggage" />
              </Field>
              <Field label="Carrier vehicle required?" required>
                <RadioGroup name="carrierVehicle" value={form.carrierVehicle}
                            onChange={(v) => setField("carrierVehicle", v)}
                            options={["Yes", "No"]} testid="field-carrierVehicle" />
              </Field>
              <Field label="Attendant required?" required>
                <RadioGroup name="attendantRequired" value={form.attendantRequired}
                            onChange={(v) => setField("attendantRequired", v)}
                            options={["Yes", "No"]} testid="field-attendantRequired" />
              </Field>
              {form.attendantRequired === "Yes" && (
                <Field label="Consent for attendant charges (₹400)" required>
                  <RadioGroup name="attendantChargesConsent" value={form.attendantChargesConsent}
                              onChange={(v) => setField("attendantChargesConsent", v)}
                              options={["Yes", "No"]} testid="field-attendantChargesConsent" />
                </Field>
              )}
            </div>
            {/* Phase 23 — optional supporting document (max 10 MB, any type). */}
            <FileField label="Supporting document (optional)"
                       fieldName="supporting_document"
                       accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xlsx,.xls"
                       attachments={attachments}
                       onChange={(e) => onUpload("supporting_document", e)}
                       disabled={uploading}
                       testid="transport-supporting-doc-upload"
                       optional />
          </Section>

          <Section numeral="V" title="Safety & emergency">
            <Field label="Any medical emergency condition">
              <textarea className="input-vgws min-h-[60px]" value={form.medicalConditions}
                        onChange={(e) => setField("medicalConditions", e.target.value)}
                        data-testid="field-medicalConditions" />
            </Field>
            <Field label="Any allergic conditions">
              <textarea className="input-vgws min-h-[60px]" value={form.allergies}
                        onChange={(e) => setField("allergies", e.target.value)}
                        data-testid="field-allergies" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Emergency contact number" required>
                <input className="input-vgws" value={form.emergencyContact}
                       onChange={(e) => setField("emergencyContact", e.target.value)}
                       data-testid="field-emergencyContact" />
              </Field>
              <Field label="Emergency contact number in school" required>
                <input className="input-vgws" value={form.schoolEmergencyContact}
                       onChange={(e) => setField("schoolEmergencyContact", e.target.value)}
                       data-testid="field-schoolEmergencyContact" />
              </Field>
            </div>
          </Section>

          <Section numeral="VI" title="Payment details">
            {/* Phase 9 (item 3a) — Trip purpose */}
            <Field label="Transportation is for" required>
              <div className="flex gap-3" data-testid="field-trip_purpose_type">
                {["official", "personnel"].map((v) => (
                  <label key={v} className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium ${form.trip_purpose_type === v ? "bg-[#7a1f2b] text-white border-[#5a1520]" : "bg-white text-[#7a1f2b] border-[#e2d7c3]"}`}>
                    <input type="radio" className="hidden" name="trip_purpose_type" value={v}
                            checked={form.trip_purpose_type === v}
                            onChange={(e) => setField("trip_purpose_type", e.target.value)}
                            data-testid={`trip_purpose_type-${v}`} />
                    {v === "official" ? "Official" : "Personnel"}
                  </label>
                ))}
              </div>
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Payment paid by" required>
                <select className="input-vgws" value={form.payment_paid_by}
                        onChange={(e) => { setField("payment_paid_by", e.target.value); setField("paymentPaidBy", e.target.value); }}
                        data-testid="field-payment_paid_by">
                  <option value="">Select</option>
                  <option value="school">School</option>
                  <option value="student_imprest">Student Imprest Account</option>
                  <option value="staff">Staff</option>
                  <option value="parent">Parents</option>
                </select>
              </Field>
              <Field label="Payment mode" required>
                <select className="input-vgws" value={form.paymentMode}
                        onChange={(e) => setField("paymentMode", e.target.value)}
                        data-testid="field-paymentMode">
                  <option value="">Select</option>
                  {PAYMENT_MODES.map((v) => <option key={v}>{v}</option>)}
                </select>
              </Field>
            </div>
            {/* Phase 9 (item 3d) — parent email + CTA */}
            {String(form.payment_paid_by || "").toLowerCase() === "parent" && (
              <div className="grid sm:grid-cols-2 gap-4 mt-4 p-4 rounded-md border border-[#b48a45] bg-[#faf5eb]">
                <Field label="Parent Email" required>
                  <input type="email" className="input-vgws" required
                          value={form.parent_email}
                          onChange={(e) => setField("parent_email", e.target.value)}
                          data-testid="field-parent_email" />
                </Field>
                <div className="flex items-end">
                  <button type="button"
                          onClick={async () => {
                            const pe = String(form.parent_email || "").trim();
                            if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(pe)) { toast.error("Enter a valid parent email first."); return; }
                            try {
                              // Only works AFTER submission; UI hint if pre-submit
                              toast.info("Payment details will be sent automatically on submit.");
                            } catch (err) { toast.error(formatApiError(err)); }
                          }}
                          className="btn-vgws"
                          data-testid="btn-send-parent-payment">
                    Send Payment Details to Parent
                  </button>
                </div>
              </div>
            )}
            <FileField label="Attach payment screenshot or receipt" fieldName="paymentReceipt"
                       accept=".pdf,.jpg,.jpeg,.png"
                       attachments={attachments} onChange={(e) => onUpload("paymentReceipt", e)}
                       disabled={uploading} testid="attach-paymentReceipt" optional />
            {/* Phase 9 (item 3b) — parent mail draft attachment */}
            <FileField label="Attach Mail Print / Parent Mail Draft" fieldName="parent_mail_draft"
                       accept=".pdf,.jpg,.jpeg,.png"
                       attachments={attachments} onChange={(e) => onUpload("parent_mail_draft", e)}
                       disabled={uploading} testid="attach-parent_mail_draft" optional />
          </Section>

          {/* Phase 9 (item 3f) — Cancel-trip toggle */}
          <Section numeral="VI·b" title="Cancel the Trip?">
            <Field label="Cancel the Trip?">
              <div className="flex gap-3" data-testid="field-trip_cancelled">
                {[[true, "Yes"], [false, "No"]].map(([v, lbl]) => (
                  <label key={String(v)}
                          className={`px-4 py-2 rounded-md border cursor-pointer text-sm font-medium ${form.trip_cancelled === v ? "bg-[#7a1f2b] text-white border-[#5a1520]" : "bg-white text-[#7a1f2b] border-[#e2d7c3]"}`}>
                    <input type="radio" className="hidden" name="trip_cancelled"
                            checked={form.trip_cancelled === v}
                            onChange={() => setField("trip_cancelled", v)}
                            data-testid={`trip_cancelled-${v ? "yes" : "no"}`} />
                    {lbl}
                  </label>
                ))}
              </div>
            </Field>
            {form.trip_cancelled === true && (
              <Field label="Cancellation Reason (min 10 chars)" required>
                <textarea className="input-vgws min-h-[60px]" required minLength={10}
                          value={form.trip_cancel_reason}
                          onChange={(e) => setField("trip_cancel_reason", e.target.value)}
                          data-testid="field-trip_cancel_reason" />
              </Field>
            )}
          </Section>

          {/* Phase 23 — Consent redesign. 10 red bulleted statements with
              a single required checkbox below. Legacy per-key `consents`
              dict is accepted by backend for historical records. */}
          <Section numeral="VII" title="Applicant consent" testid="section-consent">
            <p className="text-sm text-[#4a524c] mb-3">
              Please read all 10 statements carefully. Tick the agreement
              checkbox at the end to enable submission.
            </p>
            <ul className="list-disc pl-6 space-y-2 text-[#b3261e] text-sm leading-relaxed"
                data-testid="consent-statements">
              <li>I confirm that the Applicant/SPOC is responsible for providing correct information on this form.</li>
              <li>I understand that Sharing Cab/Taxi price may vary; I will contact the Transportation Dept Manager for details.</li>
              <li>I understand that charges depend on the Type of Vehicle & number of people travelling.</li>
              <li>I understand that any further changes in Transport, if applicable, will be deducted from the respective student's imprest account.</li>
              <li>I understand that booking will be confirmed only if the balance amount is available in the respective student's imprest account.</li>
              <li>I agree to bear the difference in charges if any trip details get modified such as vehicle type, sharing cab, group travel details, travel distance, pick-up/drop location.</li>
              <li>In case of availing for sharing a cab, the charges will be deducted post completion of travel.</li>
              <li>I (Traveler) agree to bear the cancellation charges. If cancelled 4 hours prior to travel time then zero travel charges apply; if cancelled less than 4 hours to travel time then Rs 400 cancellation charges will be applied.</li>
              <li>Airport / Railway station / Mall parking / waiting charges will be applied over & above.</li>
              <li>Note: If the form is re-edited then it will go in loop for acknowledgement & approval, which takes time.</li>
            </ul>
            <label className="flex items-start gap-2 text-sm mt-4 p-3 rounded border border-[#b48a45] bg-[#faf5eb] cursor-pointer">
              <input
                type="checkbox"
                className="mt-0.5 w-4 h-4 accent-[#7a1f2b] shrink-0"
                checked={!!form.applicant_consent_agreed}
                onChange={(e) => setField("applicant_consent_agreed", e.target.checked)}
                data-testid="transport-consent-agreed-checkbox"
              />
              <span className="text-[#1f2320] font-medium">
                I agree to all the above-mentioned terms and conditions
                <span className="text-[#b3261e]"> *</span>
              </span>
            </label>
          </Section>

          <div className="flex flex-wrap items-center justify-between gap-3 pt-2">
            <p className="text-xs text-[#6e7770] max-w-md">
              Fields marked <span className="text-[#b3261e]">*</span> are required.
              {!form.applicant_consent_agreed && (
                <span className="block text-[#b3261e] mt-1" data-testid="consent-block-hint">
                  Please tick the agreement checkbox to enable submission.
                </span>
              )}
            </p>
            <button
              type="submit"
              disabled={submitting || !form.applicant_consent_agreed}
              className="btn-vgws" data-testid="submit-transportation-button"
            >
              <Send size={16} /> {submitting ? "Submitting…" : "Submit requisition"}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
}
